import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { useMediaQuery } from '@mui/material';
import { ETFPageContainer } from 'components/layout';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import BackgroundImage from '../../../assets/images/login_background_image.png';
import { AccessibilityLogo } from '../../../components/Logo/AccessibilityLogo';
import './Login.scss';

const formFields = {
    resetPassword: {
        username: {
            label: 'Email',
            placeholder: 'Enter your email',
        },
    },
};

const headerDescriptions: Record<string, string> = {
    default: 'Enter your authorized email below.',
    resetPassword:
        "Enter your email to receive a code for resetting your password. Check your junk or spam folder if you don't see it in your inbox.",
    confirmResetPassword: 'Please enter the code emailed to you to complete the password reset process.',
};

type Step = 'email' | 'password';

export function Login({ rememberMe: _rememberMe }: { rememberMe?: React.MutableRefObject<boolean | undefined> }) {
    const { route, error, isPending, submitForm, } = useAuthenticator((context) => [
        context.route,
        context.error,
        context.isPending,
    ]);
    const isMobile = useMediaQuery('(max-width:999.5px)');

    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);

    const description = headerDescriptions[route] || headerDescriptions.default;

    const useCustomForm = route === 'signIn' || route === 'idle' || route === 'setup';

    useEffect(() => {
        if (route !== 'signIn') {
            setLocalError(null);
        }
    }, [route]);

    const handleEmailSubmit = (e: FormEvent) => {
        e.preventDefault();
        setLocalError(null);
        const trimmed = email.trim();
        if (!trimmed) {
            setLocalError('Email is required');
            return;
        }
        setStep('password');
    };

    const handlePasswordSubmit = (e: FormEvent) => {
        e.preventDefault();
        setLocalError(null);
        if (!password) {
            setLocalError('Password is required');
            return;
        }
        submitForm({ username: email.trim(), password });
    };

    const components = useMemo(
        () => ({
            Header() {
                return <AccessibilityLogo description={description} />;
            },
        }),
        [description],
    );

    const displayedError = localError || error;

    return (
        <ETFPageContainer
            containerStyles={{
                maxWidth: 'unset',
                backgroundColor: '#fff',
                minHeight: '500px',
            }}>
            <div style={{ display: 'flex', minHeight: '100vh', height: '100%', overflow: 'hidden' }}>
                <div
                    style={{
                        flex: isMobile ? '0 0 100%' : '0 0 50%',
                        backgroundImage: `url(${BackgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                <div style={{ position: 'absolute', left: '50%', right: isMobile ? '50%' : '0%', border: '50%' }}>
                    {useCustomForm ? (
                        <div data-amplify-authenticator=''>
                            <div data-amplify-container=''>
                                <div data-amplify-router=''>
                                    <div
                                        data-amplify-router-content=''
                                        style={{ flexDirection: 'column', gap: 12 }}>
                                        <AccessibilityLogo description={description} />
                                        <form
                                            data-amplify-form=''
                                            onSubmit={
                                                step === 'email' ? handleEmailSubmit : handlePasswordSubmit
                                            }
                                            noValidate>
                                            {step === 'password' && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 8,
                                                        maxWidth: 400,
                                                        width: '100%',
                                                        marginBottom: 8,
                                                        fontSize: 14,
                                                        color: isMobile ? '#fff' : '#555',
                                                    }}>
                                                    <span
                                                        style={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                        {email}
                                                    </span>
                                                    <button
                                                        type='button'
                                                        className='cancel-link'
                                                        onClick={() => {
                                                            setPassword('');
                                                            setLocalError(null);
                                                            setStep('email');
                                                        }}>
                                                        Change
                                                    </button>
                                                </div>
                                            )}
                                            <fieldset
                                                className='amplify-flex amplify-field-group'
                                                disabled={isPending}>
                                                {step === 'email' ? (
                                                    <input
                                                        key='email'
                                                        className='amplify-input amplify-input-email'
                                                        type='email'
                                                        name='username'
                                                        autoComplete='username email'
                                                        placeholder='Email'
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        autoFocus
                                                        required
                                                    />
                                                ) : (
                                                    <>
                                                        <input
                                                            type='email'
                                                            name='username'
                                                            autoComplete='username email'
                                                            value={email}
                                                            readOnly
                                                            hidden
                                                            tabIndex={-1}
                                                        />
                                                        <input
                                                            key='password'
                                                            className='amplify-input amplify-input-password'
                                                            type='password'
                                                            name='password'
                                                            autoComplete='current-password'
                                                            placeholder='Enter your Password'
                                                            value={password}
                                                            onChange={(e) => setPassword(e.target.value)}
                                                            autoFocus
                                                            required
                                                        />
                                                    </>
                                                )}
                                            </fieldset>
                                            {displayedError && (
                                                <div
                                                    className='amplify-alert amplify-alert--error'
                                                    role='alert'
                                                    style={{
                                                        maxWidth: 400,
                                                        width: '100%',
                                                        marginTop: 8,
                                                        padding: '8px 12px',
                                                        background: '#fde8e8',
                                                        color: '#b00020',
                                                        boxSizing: 'border-box',
                                                    }}>
                                                    {displayedError}
                                                </div>
                                            )}
                                            <button
                                                type='submit'
                                                className='amplify-button amplify-button--primary amplify-button--fullwidth'
                                                disabled={isPending}>
                                                {isPending ? 'Signing In...' : 'Sign In'}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Authenticator
                            formFields={formFields}
                            components={components}
                            hideSignUp={true}
                            loginMechanisms={['email']}
                        />
                    )}
                </div>
            </div>
        </ETFPageContainer>
    );
}
